/**
 * Canonical in-app viewer path for a cloud Service.
 *
 * The transcript-vs-screen-vs-session routing decision was duplicated inline at
 * several call sites (room card, source selector). They drifted: the source
 * selector never implemented the live-session branch, so selecting a transcript
 * that had an active web session routed to the transcript-only viewer and the
 * student lost the video. This helper is the single owner of the decision so a
 * new viewer route (or a change to it) lands in one place.
 *
 * Tree (mirrors the room card):
 *   - TRANSCRIPT + an active live session → /view/session/{liveDocumentId}
 *     (unified viewer: transcript + optional video)
 *   - TRANSCRIPT (no live session)        → /view/transcript/{id}
 *   - SCREEN / SCREENCAST                  → /view/screen/{id}
 *
 * Ad-hoc local sources (`/view/...?url=`) and the broadcaster's own generated
 * session URL are deliberately NOT routed through here — they carry different
 * data (a raw source URL, the broadcaster's own documentId) than a cloud
 * Service and never have a `liveDocumentId`.
 */

import { SourceType } from '@polygraf/shared'

export interface ViewableService {
	id: string
	type: SourceType
	/** Set when a transcript has an active live web session (transcript + video). */
	liveDocumentId?: string | null
}

export function viewPathForService(service: ViewableService): string {
	if (service.type === SourceType.TRANSCRIPT && service.liveDocumentId) {
		return `/view/session/${service.liveDocumentId}`
	}
	return service.type === SourceType.TRANSCRIPT
		? `/view/transcript/${service.id}`
		: `/view/screen/${service.id}`
}
